import React, { FC, useEffect } from 'react';
import { cloneElement, Fragment } from 'react';
import {
  List,
  TextField,
  EditButton,
  ShowButton,
  Filter,
  ArrayField,
  SingleFieldList,
  ChipField,
  TextInput,
  BooleanField,
  BooleanInput,
  useListContext,
  TopToolbar,
  CreateButton,
  ExportButton,
  sanitizeListRestProps,
  FilterProps,
  ListProps,
  Record,
} from 'react-admin';
import Datagrid from 'components/Datagrid';
import { exporter } from 'providers/export';
import { PermissionsProvider } from 'providers/permissionsProvider';
import {
  CAN_EDIT_CONTRACTOR_DETAILS,
  CAN_VIEW_CONTRACTOR_FORM,
} from 'providers/permissions';
import { trackEvent } from 'utils/mixpanel';

const ContractorsFilter = (props: FilterProps) => (
  <Filter {...props}>
    <TextInput
      label="Name"
      source="name||$cont"
      data-cy="contractorFilterName"
    />
    <TextInput
      label="Corporation"
      source="corporation||$cont"
      data-cy="contractorFilterCorporation"
    />
    <BooleanInput
      label="Hanger"
      source="hangerAvailable||$eq"
      data-cy="contractorFilterHanger"
    />
    <BooleanInput
      label="Finisher"
      source="finisherAvailable||$eq"
      data-cy="contractorFilterFinisher"
    />
    <BooleanInput
      label="Sprayer"
      source="sprayerAvailable||$eq"
      data-cy="contractorFilterSprayer"
    />
    <BooleanInput
      label="Paint"
      source="paintAvailable||$eq"
      data-cy="contractorFilterPaint"
    />
    <BooleanInput
      label="Trim"
      source="trimAvailable||$eq"
      data-cy="contractorFilterTrim"
    />
    <BooleanInput
      label="Bumper"
      source="bumperAvailable||$eq"
      data-cy="contractorFilterBumper"
    />
    <BooleanInput
      label="Active"
      alwaysOn
      source="active||$eq"
      data-cy="contractorFilterActive"
    />
    <BooleanInput
      label="Specialist"
      source="specialistAvailable||$eq"
      data-cy="contractorFilterSpecialist"
    />
  </Filter>
);

const ListActions = props => {
  const { className, filters, maxResults, ...rest } = props;
  const {
    currentSort,
    resource,
    displayedFilters,
    filterValues,
    basePath,
    showFilter,
    total,
  } = useListContext();

  const { hasPermission } = PermissionsProvider.useContainer();

  return (
    <TopToolbar className={className} {...sanitizeListRestProps(rest)}>
      {filters &&
        cloneElement(filters, {
          resource,
          showFilter,
          displayedFilters,
          filterValues,
          context: 'button',
        })}
      {hasPermission(CAN_EDIT_CONTRACTOR_DETAILS) ? (
        <CreateButton basePath={basePath} />
      ) : (
        <Fragment />
      )}
      <ExportButton
        disabled={total === 0}
        resource={resource}
        sort={currentSort}
        filterValues={filterValues}
        maxResults={maxResults}
      />
    </TopToolbar>
  );
};

const checkSuspended = (record: Record) => {
  return {
    backgroundColor: !record.suspended ? 'white' : '#ff8282',
  };
};

export const ContractorsList: FC<ListProps> = props => {
  const { hasPermission } = PermissionsProvider.useContainer();
  useEffect(() => {
    trackEvent('PageView', {
      resource: 'contractors',
      page: window.location.pathname,
      role: localStorage.getItem('userRoles'),
      env: process.env.REACT_APP_MIXPANEL_ENV,
    });
  }, []);
  return (
    <List
      {...props}
      exporter={exporter('contractor')}
      filters={<ContractorsFilter>{props.filters}</ContractorsFilter>}
      filterDefaultValues={{ 'active||$eq': true }}
      filter={{
        $join: [
          {
            field: 'contractorRegions',
            select: ['id', 'contractorId', 'regionId', 'region'],
          },
          { field: 'contractorRegions.region', select: ['id', 'name'] },
        ],
      }}
      sort={{ field: 'name', order: 'ASC' }}
      bulkActionButtons={<React.Fragment />}
      actions={<ListActions />}
    >
      <Datagrid
        rowClick={hasPermission(CAN_VIEW_CONTRACTOR_FORM) ? 'edit' : 'show'}
        rowStyle={checkSuspended}
      >
        <TextField source="name" />
        <TextField source="corporation" label="Corporation" />
        <TextField source="paymentType" label="Payment Type" />
        <TextField source="paymentRate" label="Payment Rate" />
        <TextField source="bumperPaymentType" label="Bumper Payment Type" />
        <TextField source="bumperPaymentRate" label="Bumper Payment Rate" />
        <BooleanField label="Hanger" source="hangerAvailable" />
        <BooleanField label="Finisher" source="finisherAvailable" />
        <BooleanField label="Sprayer" source="sprayerAvailable" />
        <BooleanField label="Paint" source="paintAvailable" />
        <BooleanField label="Trim" source="trimAvailable" />
        <BooleanField label="Bumper" source="bumperAvailable" />
        <BooleanField label="Specialist" source="specialistAvailable" />
        <ArrayField source="contractorRegions" fieldKey="id" sortable={false}>
          <SingleFieldList linkType={false}>
            <ChipField source="region.name" />
          </SingleFieldList>
        </ArrayField>
        {hasPermission(CAN_VIEW_CONTRACTOR_FORM) ? (
          <EditButton />
        ) : (
          <ShowButton />
        )}
      </Datagrid>
    </List>
  );
};
